import {
  Heading,
  RadioGroup,
  RadioItem,
  Stack,
  Textarea,
  useResponsiveValue,
} from 'braid-design-system';
import { Children, type ComponentProps, type MouseEvent, useId } from 'react';
import { Controller, type Validate } from 'react-hook-form';

import type { Resume } from 'src/__generated__/graphql';
import { DocumentSelect } from 'src/pages/Documents/components/DocumentSelect/DocumentSelect';
import DocumentUpload from 'src/pages/Documents/components/DocumentUpload/DocumentUpload';
import { isFile } from 'src/utils';

export enum FileAttachment {
  UPLOAD = 'upload',
  CHANGE = 'change',
  NONE = 'none',
}

interface AttachmentSelectProps {
  children: React.ReactNode;
  title: string;
  hideTitle?: boolean;
  name: string;
  labelNone: string;
  defaultValue?: FileAttachment;
  onChange?: (method: FileAttachment) => void;
  validate?: Validate<any, any>;
}

export function AttachmentSelect({
  children,
  title,
  hideTitle,
  name,
  labelNone,
  defaultValue,
  onChange,
  validate,
}: AttachmentSelectProps) {
  const id = useId();

  return (
    <Stack space="large">
      {!hideTitle && <Heading level="3">{title}</Heading>}
      <Controller
        name={name}
        defaultValue={defaultValue}
        rules={{ validate }}
        render={({ field, fieldState }) => (
          <RadioGroup
            aria-label={title}
            id={`${field.name}-${id}`}
            name={field.name}
            value={field.value}
            onChange={(e) => {
              const method = e.currentTarget.value as FileAttachment;
              field.onChange(method);
              onChange?.(method);
            }}
            tone={fieldState.invalid ? 'critical' : 'positive'}
            message={fieldState.error?.message}
          >
            <>
              {Children.map(children, (child) => {
                if (!isAttachmentOption(child)) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    'AttachmentSelect only accepts AttachmentOption as children',
                  );
                  return null;
                }

                let method = field.value;
                if (isAttachmentOptionFile(child)) {
                  method = FileAttachment.UPLOAD;
                }
                if (
                  isAttachmentOptionText(child) ||
                  isAttachmentOptionCombobox(child)
                ) {
                  method = FileAttachment.CHANGE;
                }

                return (
                  <RadioItem
                    key={method}
                    data={{ testid: `${name}-${method}` }}
                    label={child.props.label}
                    value={method}
                  >
                    {child}
                  </RadioItem>
                );
              })}
            </>
            <RadioItem
              key={FileAttachment.NONE}
              data={{ testid: `${name}-${FileAttachment.NONE}` }}
              label={labelNone}
              value={FileAttachment.NONE}
            />
          </RadioGroup>
        )}
      />
    </Stack>
  );
}

function isAttachmentOption(
  element: any,
): element is React.ReactElement<AttachmentOptionProps> {
  return element.props && element.props.name && element.props.label;
}

function isAttachmentOptionFile(
  element: any,
): element is React.ReactElement<ComponentProps<typeof AttachmentOptionFile>> {
  return element.type === AttachmentOptionFile;
}

function isAttachmentOptionText(
  element: any,
): element is React.ReactElement<ComponentProps<typeof AttachmentOptionText>> {
  return element.type === AttachmentOptionText;
}

function isAttachmentOptionCombobox(
  element: any,
): element is React.ReactElement<
  ComponentProps<typeof AttachmentOptionCombobox>
> {
  return element.type === AttachmentOptionCombobox;
}

interface AttachmentOptionProps<T = string> {
  name: string;
  label: string;
  validate?: Validate<any, any>;
  testid?: string;
  defaultValue?: T;
  onChange?: (value?: T) => void;
  description?: string;
  message?: string;
}

export function AttachmentOptionFile({
  name,
  validate,
  defaultValue,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  label,
  reUploadText,
  testid,
  onChange,
  onClick,
  description = '',
  isLoading,
  message,
}: {
  isLoading?: boolean;
  reUploadText: string;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
} & AttachmentOptionProps<File>) {
  const id = useId();
  return (
    <div data-testid={testid}>
      <Controller
        name={name}
        defaultValue={isFile(defaultValue) && defaultValue}
        rules={{ validate }}
        render={({ field, fieldState }) => (
          <DocumentUpload
            id={`${name}-${id}`}
            name={name}
            reUploadText={reUploadText}
            description={description}
            value={field.value}
            isLoading={isLoading}
            onClick={onClick}
            onChange={(file) => {
              onChange?.(file);
              field.onChange(file);
            }}
            valid={!fieldState.invalid}
            message={message || fieldState.error?.message || ''}
          />
        )}
      />
    </div>
  );
}

export function AttachmentOptionText({
  name,

  label,
  defaultValue,
  description,
  validate,
  limit,
  testid,
  onChange,
  message,
}: {
  limit?: number;
} & AttachmentOptionProps<string>) {
  const id = useId();

  const responsiveValue = useResponsiveValue();

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={{ validate }}
      render={({ field, fieldState }) => (
        <Textarea
          id={`${name}-${id}`}
          data={testid ? { testid } : {}}
          aria-label={label}
          ref={field.ref}
          description={description}
          value={field.value}
          onChange={(e) => {
            onChange?.(e.currentTarget.value);
            field.onChange(e);
          }}
          onBlur={field.onBlur}
          lines={6}
          lineLimit={responsiveValue({ mobile: 150, tablet: 80 }) || undefined}
          characterLimit={limit}
          tone={fieldState.invalid ? 'critical' : 'positive'}
          message={message || fieldState.error?.message}
        />
      )}
    />
  );
}

export function AttachmentOptionCombobox({
  testid,
  name,
  defaultValue,
  onChange,
  resumes,
  validate,
  message,
}: { resumes: Resume[] } & AttachmentOptionProps<string>) {
  const id = useId();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={{ validate }}
      render={({ field, fieldState }) => (
        <div data-testid={testid}>
          <DocumentSelect
            id={id}
            documentList={resumes}
            value={field.value}
            onChange={(e) => {
              field.onChange(e);
              onChange?.(e);
            }}
            defaultValue={defaultValue}
            valid={!message && !fieldState.invalid}
            message={message || fieldState.error?.message || ''}
          />
        </div>
      )}
    />
  );
}
